import { FetchClient } from '../../../clients/FetchClient';
import { WEBSITE_SETTINGS_PATH } from '../../../constants';

interface AriaChatSettings extends Record<string, string> {
    region: string;
    ariaRuntimeArn: string;
}

export type AriaStreamEvent = { type: 'text'; text: string } | { type: 'break' } | { type: 'error'; message: string };

export const buildInvokeUrl = (region: string, runtimeArn: string): string =>
    `https://bedrock-agentcore.${region}.amazonaws.com/runtimes/${encodeURIComponent(
        runtimeArn
    )}/invocations?qualifier=DEFAULT`;

export const fetchInvokeUrl = async (): Promise<string> => {
    const settings = await FetchClient.fetch<AriaChatSettings>(WEBSITE_SETTINGS_PATH);
    if (!settings.ariaRuntimeArn) {
        throw new Error('ariaRuntimeArn is missing from website settings');
    }
    return buildInvokeUrl(settings.region, settings.ariaRuntimeArn);
};

/**
 * Maps the JSON payload of one server-sent event from the Aria runtime to a widget event.
 * The runtime streams every Strands agent event; only model text deltas, the end of each model
 * message, and errors are relevant to the widget, so anything else returns undefined.
 */
export const parseStreamEvent = (data: string): AriaStreamEvent | undefined => {
    let parsed: any;
    try {
        parsed = JSON.parse(data);
    } catch {
        return undefined;
    }
    if (!parsed || typeof parsed !== 'object') {
        return undefined;
    }

    const text = parsed.event?.contentBlockDelta?.delta?.text;
    if (typeof text === 'string') {
        return { type: 'text', text };
    }

    // The agent can reply in several model messages (e.g. text, then a knowledge base search, then more text)
    if (parsed.event?.messageStop) {
        return { type: 'break' };
    }

    // Errors raised by the agent entrypoint
    const agentErrorMessage = parsed.event?.error?.message;
    if (typeof agentErrorMessage === 'string') {
        return { type: 'error', message: agentErrorMessage };
    }

    // Errors raised by the AgentCore runtime SDK while streaming
    if (parsed.error !== undefined && typeof parsed.message === 'string') {
        return { type: 'error', message: parsed.message };
    }

    return undefined;
};

/**
 * Incrementally splits a text/event-stream body into the data payloads of complete events.
 * Network chunks can end mid-event, so incomplete input is buffered until its blank-line terminator arrives.
 */
export class ServerSentEventParser {
    private buffer = '';

    public push(chunk: string): string[] {
        this.buffer += chunk.replace(/\r\n/g, '\n');

        const payloads: string[] = [];
        let separatorIndex = this.buffer.indexOf('\n\n');
        while (separatorIndex !== -1) {
            const rawEvent = this.buffer.slice(0, separatorIndex);
            this.buffer = this.buffer.slice(separatorIndex + 2);

            const data = rawEvent
                .split('\n')
                .filter((line) => line.startsWith('data:'))
                .map((line) => line.slice('data:'.length).replace(/^ /, ''))
                .join('\n');
            if (data) {
                payloads.push(data);
            }

            separatorIndex = this.buffer.indexOf('\n\n');
        }
        return payloads;
    }
}
